import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../components/DateRangePicker';
import InspectorHoverCard from '../components/InspectorHoverCard';
import ImportZipModal from '../components/ImportZipModal';
import { useAuth } from '../contexts/AuthContext';

type InspectionStage = 'Intro' | 'Dupro' | 'Final' | 'Re-Final';

interface FinalInspectionRecord {
  id: string;
  po_number: string;
  customer_name: string;
  product_category: string;
  defect_count: number;
  rawDate: string;
  dateLabel: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  result: 'PASS' | 'FAIL' | 'PENDING';
  inspection_type: InspectionStage;
  is_recovered: boolean;
  recovery_incomplete: boolean;
  inspector: {
    name: string;
    initials: string;
    color: string;
    photo?: string;
    inspectorId: string | null;
  };
}

const normalizeInspectionType = (raw: unknown): InspectionStage => {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'intro') return 'Intro';
  if (v === 'dupro' || v === 'duopro') return 'Dupro';
  if (v === 're-final' || v === 'refinal' || v === 're_final') return 'Re-Final';
  return 'Final';
};

const getInitials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const getAvatarColor = (id: string) => {
  const colors = [
    'bg-blue-100 text-blue-600',
    'bg-emerald-100 text-emerald-600',
    'bg-purple-100 text-purple-600',
    'bg-amber-100 text-amber-600',
    'bg-rose-100 text-rose-600',
    'bg-indigo-100 text-indigo-600',
    'bg-slate-200 text-slate-600'
  ];
  const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
};

const mapFinalStatus = (r: FinalInspectionRecord): 'Passed' | 'Failed' | 'In Progress' | 'Pending' => {
  if (r.status !== 'COMPLETED') return r.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending';
  if (r.result === 'PASS') return 'Passed';
  if (r.result === 'FAIL') return 'Failed';
  return 'Pending';
};

const FinalInspectionReports: React.FC = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const [reports, setReports] = useState<FinalInspectionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState('All');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [importFile, setImportFile] = useState<File | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const importInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/final-inspections`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.status === 401) {
          logout();
          return;
        }
        const data = await response.json();
        if (data.success) {
          const mapped: FinalInspectionRecord[] = data.data.map((r: any) => ({
            id: r.id,
            po_number: r.po_number || 'N/A',
            customer_name: r.customer_name || 'N/A',
            product_category: r.product_category || 'N/A',
            defect_count: r.defect_count || 0,
            rawDate: r.created_at,
            dateLabel: new Date(r.created_at).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }),
            status: r.status,
            result: r.overall_result,
            inspection_type: normalizeInspectionType(r.inspection_type ?? r.inspectionType),
            is_recovered: !!r.is_recovered,
            recovery_incomplete: !!r.recovery_incomplete,
            inspector: {
              name: r.inspector?.name || r.inspector_name || 'Unknown',
              initials: getInitials(r.inspector?.name || r.inspector_name || 'Unknown'),
              color: getAvatarColor(r.inspector?.id || 'default'),
              photo: r.inspector?.profileImage || undefined,
              inspectorId: r.inspector?.id || null,
            },
          }));
          setReports(mapped);
        }
      } catch (err) {
        console.error('Failed to fetch final inspection reports:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchReports();
  }, [token, refreshTick]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const uniqueUserObjects = [
    { name: 'All', photo: undefined, color: '', initials: '' },
    ...(Array.from(new Map(reports.map(r => [r.inspector.name, r.inspector])).values())),
  ];

  const filteredReports = reports.filter(r => {
    const finalStatus = mapFinalStatus(r);

    if (activeFilter === 'Intro' && r.inspection_type !== 'Intro') return false;
    if (activeFilter === 'Dupro' && r.inspection_type !== 'Dupro') return false;
    if (activeFilter === 'Final' && r.inspection_type !== 'Final' && r.inspection_type !== 'Re-Final') return false;
    if (activeFilter === 'Final' && finalStatus === 'Pending') return false;

    const matchesUser = selectedUser === 'All' || r.inspector.name === selectedUser;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      r.id.toLowerCase().includes(q) ||
      r.customer_name.toLowerCase().includes(q) ||
      r.po_number.toLowerCase().includes(q);

    const reportDate = new Date(r.rawDate);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    const matchesDate = (!from || reportDate >= from) && (!to || reportDate <= to);

    return matchesUser && matchesSearch && matchesDate;
  });

  const groupedReports = (() => {
    const map = new Map<string, { key: string; po_number: string; customer_name: string; latestDate: string; reports: FinalInspectionRecord[] }>();
    filteredReports.forEach(r => {
      const key = `${r.po_number}||${r.customer_name}`;
      const existing = map.get(key);
      if (existing) {
        existing.reports.push(r);
        if (new Date(r.rawDate) > new Date(existing.latestDate)) existing.latestDate = r.rawDate;
      } else {
        map.set(key, { key, po_number: r.po_number, customer_name: r.customer_name, latestDate: r.rawDate, reports: [r] });
      }
    });
    return Array.from(map.values()).sort((a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime());
  })();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter, selectedUser, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(groupedReports.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedGroups = groupedReports.slice(indexOfFirstItem, indexOfLastItem);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Passed': return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
      case 'Failed': return 'bg-red-50 text-red-700 ring-red-600/20';
      case 'In Progress': return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      case 'Scheduled': return 'bg-blue-50 text-blue-700 ring-blue-600/20';
      case 'Pending': return 'bg-slate-100 text-slate-500 ring-slate-400/20';
      default: return 'bg-slate-100 text-slate-500 ring-slate-400/20';
    }
  };

  const getDotClass = (status: string) => {
    switch (status) {
      case 'Passed': return 'bg-emerald-600';
      case 'Failed': return 'bg-red-600';
      case 'In Progress': return 'bg-blue-600';
      case 'Scheduled': return 'bg-blue-600';
      case 'Pending': return 'bg-slate-400';
      default: return 'bg-slate-400';
    }
  };

  const renderStageCell = (reports: FinalInspectionRecord[], stage: 'Intro' | 'Dupro' | 'Final') => {
    const stageReports = reports.filter(r =>
      stage === 'Final'
        ? r.inspection_type === 'Final' || r.inspection_type === 'Re-Final'
        : r.inspection_type === stage
    );

    const isFinalCol = stage === 'Final';

    if (stageReports.length === 0) {
      return (
        <td className={`px-6 py-4 align-top border-l ${isFinalCol ? 'border-r' : ''} border-slate-100`}>
          <div className="flex items-center justify-center h-full min-h-[72px]">
            <span className="text-xs font-medium text-slate-400">N/A</span>
          </div>
        </td>
      );
    }

    return (
      <td className={`px-6 py-4 align-top border-l ${isFinalCol ? 'border-r' : ''} border-slate-100`}>
        <div className="flex flex-col gap-3">
          {stageReports.map(report => {
            const finalStatus = mapFinalStatus(report);
            const showRefinalTag = isFinalCol && report.inspection_type === 'Re-Final';
            return (
              <div
                key={report.id}
                onClick={() => navigate(`/final-inspection-report/${report.id}`)}
                className={`group/tile relative flex flex-col gap-2 rounded-lg border p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${finalStatus === 'Failed' ? 'border-red-100 bg-red-50/30' : 'border-slate-200 bg-white'} ${finalStatus === 'Pending' ? 'opacity-60' : ''}`}
              >
                {finalStatus !== 'Pending' && (
                  <span className={`material-symbols-outlined absolute right-2 top-2 text-[18px] transition-colors ${finalStatus === 'Failed' ? 'text-red-300 group-hover/tile:text-red-600' : 'text-slate-300 group-hover/tile:text-[#136bce]'}`}>arrow_forward</span>
                )}
                <div className="flex items-center justify-between pr-4 flex-wrap gap-1.5">
                  <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ring-1 ring-inset ${getStatusClass(finalStatus)}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${getDotClass(finalStatus)}`}></span>
                    {finalStatus}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {report.is_recovered && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${report.recovery_incomplete ? 'bg-amber-50 text-amber-700 ring-amber-600/20' : 'bg-blue-50 text-[#136bce] ring-blue-600/20'}`}
                      >
                        <span className="material-symbols-outlined text-[12px]">restore_page</span>
                        {report.recovery_incomplete ? 'Recovered · Incomplete' : 'Recovered'}
                      </span>
                    )}
                    {showRefinalTag && (
                      <span className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                        Re-Final
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[14px] text-slate-400">event</span>
                    <span className="text-[11px] font-medium">{report.dateLabel}</span>
                  </div>
                  <span className="font-mono text-[10px] font-semibold text-slate-400">
                    {report.id.substring(0, 8).toUpperCase()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </td>
    );
  };

  return (
    <div className="mx-auto w-full">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Final Inspection Reports</h1>
          <p className="text-sm text-slate-500">Manage final inspection stages and outcomes</p>
        </div>
        {isAdmin && (
          <>
            <button
              onClick={() => importInputRef.current?.click()}
              className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-500/30 transition-all hover:bg-blue-700 active:scale-95 shrink-0"
            >
              <span className="material-symbols-outlined text-[18px]">upload_file</span>
              Import
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f) setImportFile(f);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {importFile && token && (
        <ImportZipModal
          file={importFile}
          token={token}
          onClose={() => setImportFile(null)}
          onSuccess={(inspectionId) => {
            setImportFile(null);
            setRefreshTick((t) => t + 1);
            navigate(`/final-inspection-report/${inspectionId}`);
          }}
        />
      )}

      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center">
          <div className="relative w-full md:max-w-md">
            <input
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm placeholder-slate-400 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] outline-none"
              placeholder="Search by PO, Customer or ID..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="material-symbols-outlined absolute left-3 top-2.5 text-[20px] text-slate-400">search</span>
          </div>
          <div className="relative inline-block" ref={userDropdownRef}>
            <button
              onClick={() => setIsUserDropdownOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer min-w-[140px]"
            >
              {selectedUser === 'All' ? (
                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[14px] text-slate-500">group</span>
                </span>
              ) : (() => {
                const u = uniqueUserObjects.find(x => x.name === selectedUser);
                return u?.photo ? (
                  <img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u?.color || 'bg-slate-200 text-slate-600'}`}>
                    {u?.initials || selectedUser[0]}
                  </span>
                );
              })()}
              <span className="truncate">{selectedUser === 'All' ? 'All Users' : selectedUser}</span>
              <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
            </button>
            {isUserDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 z-20 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto">
                {uniqueUserObjects.map(u => (
                  <button key={u.name} onClick={() => { setSelectedUser(u.name); setIsUserDropdownOpen(false); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedUser === u.name ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}>
                    {u.name === 'All' ? (<span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><span className="material-symbols-outlined text-[14px] text-slate-500">group</span></span>) : u.photo ? (<img src={u.photo} alt={u.name} className="h-6 w-6 rounded-full object-cover border border-slate-200 shrink-0" />) : (<span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${u.color}`}>{u.initials}</span>)}
                    {u.name === 'All' ? 'All Users' : u.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-medium text-slate-700">Filter Date:</span>
          <DateRangePicker range={{ from: dateFrom, to: dateTo }} onChange={(range) => { setDateFrom(range.from); setDateTo(range.to); }} onClear={() => { setDateFrom(''); setDateTo(''); }} />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {['All', 'Intro', 'Dupro', 'Final'].map((filter) => (
          <button key={filter} onClick={() => setActiveFilter(filter)} className={`rounded-lg px-5 py-2.5 text-sm font-medium transition-all shadow-sm ${activeFilter === filter ? 'bg-[#136bce] text-white shadow-blue-500/30' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
            {filter}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200 align-bottom" rowSpan={2}>Customer Name</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200 align-bottom" rowSpan={2}>PO Number</th>
                <th className="px-6 py-4 font-semibold tracking-wide border-b border-slate-200 align-bottom" rowSpan={2}>QA Executive</th>
                <th className="px-6 py-3 font-semibold tracking-wide text-center border-b border-slate-200 border-l border-r" colSpan={3}>Inspection Stages</th>
              </tr>
              <tr>
                <th className="px-6 py-3 font-semibold tracking-wide border-l border-slate-200 bg-slate-100/50 w-[200px]">Intro</th>
                <th className="px-6 py-3 font-semibold tracking-wide border-l border-slate-200 bg-slate-100/50 w-[200px]">Dupro</th>
                <th className="px-6 py-3 font-semibold tracking-wide border-l border-r border-slate-200 bg-slate-100/50 w-[200px]">Final</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-[48px] animate-spin">progress_activity</span>
                      <p className="font-semibold text-sm">Loading reports...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedGroups.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-[48px]">find_in_page</span>
                      <p className="font-semibold text-sm">No reports match your filters</p>
                      <p className="text-xs">Try adjusting your search or date range.</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedGroups.map((group) => {
                const uniqueInspectors = Array.from(
                  new Map(group.reports.map(r => [r.inspector.inspectorId ?? r.inspector.name, r.inspector])).values()
                );
                return (
                  <tr key={group.key} className="group transition-colors hover:bg-blue-50/20">
                    <td className="px-6 py-4 align-top font-semibold text-slate-900">{group.customer_name}</td>
                    <td className="px-6 py-4 align-top text-slate-700">{group.po_number}</td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-2">
                        {uniqueInspectors.map(ins => (
                          <InspectorHoverCard key={ins.inspectorId ?? ins.name} inspectorId={ins.inspectorId} name={ins.name} initials={ins.initials} color={ins.color} photo={ins.photo} token={token || ''}>
                            <div className="flex items-center gap-2">
                              {ins.photo ? (<img src={ins.photo} alt={ins.name} className="h-6 w-6 rounded-full object-cover" />) : (<span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${ins.color}`}>{ins.initials}</span>)}
                              <span className="text-slate-700">{ins.name}</span>
                            </div>
                          </InspectorHoverCard>
                        ))}
                      </div>
                    </td>
                    {renderStageCell(group.reports, 'Intro')}
                    {renderStageCell(group.reports, 'Dupro')}
                    {renderStageCell(group.reports, 'Final')}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium">{groupedReports.length === 0 ? 0 : indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, groupedReports.length)}</span> of <span className="font-medium">{groupedReports.length}</span> results
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-[#136bce] text-white shadow-sm ring-1 ring-[#136bce]' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalInspectionReports;
